// Deliberate, named signals to Datadog RUM.
//
// Not general-purpose logging. This exists for the handful of events where an
// operator must find out WITHOUT a person noticing and reporting it — the
// browser's console reaches nobody, and this app ships `@datadog/browser-rum`
// only (no `@datadog/browser-logs`, no `forwardConsoleLogs`), so a
// `console.warn` is invisible outside the tab it happened in.
//
// That gap is why infra/datadog/monitors.tf carries two monitors that CANNOT
// FIRE (H2-1548, H2-2009): both grep for strings nothing emits. A monitor is
// only as real as the signal under it, so the signal comes first.
//
// `addError` rather than `addAction`: these are defects, and error grade is
// what puts them in Error Tracking, groups them by message, and makes them
// alertable with a `rum alert` monitor.

import { datadogRum } from "@datadog/browser-rum";

/**
 * Report a named client-side defect to RUM.
 *
 * Safe everywhere by construction: no-ops during SSR, and swallows anything
 * the SDK throws when RUM was never initialised (tests, local dev without a
 * client token). Telemetry must never be able to break the path it observes —
 * this is called from inside the messages-cache reconciler, and an exception
 * here would take out the very transcript write it is reporting on.
 */
export function reportClientDefect(
  name: string,
  context: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") return;
  try {
    datadogRum.addError(new Error(name), { defect: name, ...context });
  } catch {
    // RUM absent or not initialised. Nothing to do, and nothing worth saying:
    // the caller has already logged to the console.
  }
}
